import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, IconButton, Tooltip, TextField,
  Dialog, Backdrop, Fade, Button, CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import ScaleOutlinedIcon from "@mui/icons-material/ScaleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { updateCarritoByID } from "../../../services/CarritoServices";

// ═══════════════════════════════════════
// QtyControl (sin cambios)
// ═══════════════════════════════════════
const QtyControl = ({ item, changeAmount, isCheckStock, compact = false }) => {
  const sz = compact ? 26 : 30;
  const isFrac = !!item.fractional;
  const step = isFrac ? 0.25 : 1;
  const maxQty = isCheckStock ? item.availableStock : 99999;
  const maxReached = isCheckStock && item.amount >= item.availableStock;
  const [localQty, setLocalQty] = useState(item.amount);

  useEffect(() => { setLocalQty(item.amount); }, [item.amount]);

  const handleChange = (e) => {
    const value = e.target.value;
    if (isFrac) {
      const normalized = value.replace(',', '.');
      if (normalized === '' || normalized === '.' || /^\d*\.?\d{0,2}$/.test(normalized)) {
        setLocalQty(normalized === '' ? '' : normalized);
      }
    } else {
      const raw = value.replace(/\D/g, "");
      if (raw === "") { setLocalQty(""); return; }
      setLocalQty(Math.min(Number(raw), maxQty));
    }
  };

  const handleBlur = () => {
    let final = parseFloat(localQty) || (isFrac ? 0.01 : 1);
    if (isFrac) {
      if (final < 0.01) final = 0.01;
      final = Math.round(final * 100) / 100;
    } else {
      if (final < 1) final = 1;
      final = Math.floor(final);
    }
    if (final > maxQty) final = maxQty;
    setLocalQty(final);
    const diff = Math.round((final - item.amount) * 100) / 100;
    if (diff !== 0) changeAmount(diff, item.id);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") e.target.blur(); };

  const handleDecrease = () => {
    const next = Math.round((item.amount - step) * 100) / 100;
    if (next >= (isFrac ? 0.01 : 1)) changeAmount(-step, item.id);
  };

  const handleIncrease = () => {
    if (!maxReached) changeAmount(step, item.id);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", bgcolor: "var(--bg-subtle)", borderRadius: "var(--radius-sm)", border: "1.5px solid var(--border)", p: "3px", gap: '1px' }}>
      {/* Badge fraccionable */}
      {isFrac && (
        <Tooltip title="Fraccionable (permite decimales)" arrow>
          <Box sx={{ width: sz, height: sz, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', bgcolor: '#faf5ff', border: '1px solid #e9d5ff', flexShrink: 0, mr: '2px' }}>
            <ScaleOutlinedIcon sx={{ fontSize: 13, color: '#7c3aed' }} />
          </Box>
        </Tooltip>
      )}

      <IconButton size="small" onClick={handleDecrease} disabled={(isFrac ? item.amount <= 0.01 : item.amount <= 1)}
        sx={{ width: sz, height: sz, borderRadius: "6px", color: "var(--text-secondary)", "&:hover": { bgcolor: "var(--border)" }, "&:disabled": { opacity: 0.35 } }}>
        <RemoveIcon sx={{ fontSize: 13 }} />
      </IconButton>

      <TextField value={localQty} size="small" variant="standard" onChange={handleChange} onBlur={handleBlur} onKeyDown={handleKeyDown} onClick={(e) => e.target.select()}
        inputProps={{
          style: { textAlign: "center", fontWeight: 700, fontSize: "0.82rem", fontFamily: "var(--font-mono)", padding: 0 },
          inputMode: isFrac ? "decimal" : "numeric",
          pattern: isFrac ? "[0-9]*[.,]?[0-9]*" : "[0-9]*",
        }}
        sx={{
          width: Math.max(compact ? 36 : 44, String(localQty).length * 10 + 20),
          maxWidth: 80,
          '& .MuiInput-underline:before': { borderBottom: 'none' },
          '& .MuiInput-underline:after': { borderBottom: '2px solid var(--accent)' },
          '& .MuiInput-underline:hover:before': { borderBottom: '1px solid var(--border-hover) !important' },
          '& .MuiInputBase-input': { padding: '2px 0' },
        }}
      />

      <Tooltip title={maxReached ? "Stock máximo" : "Agregar uno más"} arrow><span>
        <IconButton size="small" onClick={handleIncrease} disabled={maxReached}
          sx={{ width: sz, height: sz, borderRadius: "6px", color: "var(--accent)", "&:hover": { bgcolor: "var(--accent-soft)" }, "&:disabled": { opacity: 0.35 } }}>
          <AddIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </span></Tooltip>
    </Box>
  );
};

// ═══════════════════════════════════════
// Modal de Observación
// ═══════════════════════════════════════
const ObservacionModal = ({ open, onClose, item }) => {
  const [text, setText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [status, setStatus] = useState('idle');
  const timerRef = useRef(null);
  const lastSavedRef = useRef({}); // { [itemId]: 'texto guardado' }

  // Cargar observación: usa la última guardada si existe, sino la del item
  useEffect(() => {
    if (open && item?.id) {
      const obs = String(lastSavedRef.current[item.id] ?? item?.observacion ?? '');
      setText(obs);
      setSavedText(obs);
      setStatus(obs ? 'saved' : 'idle');
    }
  }, [open, item?.id]);

  // Limpiar timer al desmontar
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleChange = (e) => {
    if (e.target.value.length > 1000) return;
    const val = e.target.value;
    setText(val);
    setStatus('typing');

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => autoSave(val), 800);
  };

  const autoSave = async (val) => {
    if (!item?.id) return;
    setStatus('saving');
    try {
      await updateCarritoByID(item.id, { cantidad: item.amount, observacion: val });
      const prevSaved = savedText;
      setSavedText(val);
      lastSavedRef.current[item.id] = val; // ← persistir entre aperturas
      if (val === '') setStatus('deleted');
      else if (prevSaved === '') setStatus('added');
      else setStatus('modified');
      setTimeout(() => setStatus(val ? 'saved' : 'idle'), 2500);
    } catch (e) {
      console.error("Error guardando observación:", e);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const handleClear = () => {
    setText('');
    setStatus('typing');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => autoSave(''), 300);
  };

  const statusConfig = {
    idle: null,
    typing: { icon: <EditNoteOutlinedIcon sx={{ fontSize: 13 }} />, label: 'Escribiendo...', bg: 'var(--bg-subtle)', border: 'var(--border)', color: 'var(--text-muted)' },
    saving: { icon: <CloudSyncOutlinedIcon sx={{ fontSize: 13, animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />, label: 'Guardando...', bg: 'var(--warning-bg)', border: 'var(--warning)', color: 'var(--warning)' },
    saved: { icon: <CloudDoneOutlinedIcon sx={{ fontSize: 13 }} />, label: 'Guardado', bg: 'var(--success-bg)', border: 'var(--success)', color: 'var(--success)' },
    added: { icon: <CloudDoneOutlinedIcon sx={{ fontSize: 13 }} />, label: 'Observación agregada', bg: '#eff6ff', border: '#3b82f6', color: '#3b82f6' },
    modified: { icon: <CloudDoneOutlinedIcon sx={{ fontSize: 13 }} />, label: 'Observación modificada', bg: '#faf5ff', border: '#7c3aed', color: '#7c3aed' },
    deleted: { icon: <DeleteOutlineIcon sx={{ fontSize: 13 }} />, label: 'Observación eliminada', bg: 'var(--danger-bg)', border: 'var(--danger)', color: 'var(--danger)' },
    error: { icon: <CloudSyncOutlinedIcon sx={{ fontSize: 13 }} />, label: 'Error al guardar', bg: 'var(--danger-bg)', border: 'var(--danger)', color: 'var(--danger)' },
  };

  const st = statusConfig[status];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      BackdropComponent={Backdrop}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' } }}
      PaperProps={{ sx: { borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StickyNote2OutlinedIcon sx={{ color: 'var(--accent)', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
              Observación
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.68rem', color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item?.title}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth multiline rows={4}
          placeholder="Escribí una observación para este artículo..."
          value={text}
          onChange={handleChange}
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '0.88rem',
              backgroundColor: 'var(--bg-subtle)', transition: 'all 0.2s ease',
              '& fieldset': {
                border: status === 'saving' ? '2px solid var(--warning)' :
                  (status === 'added' || status === 'modified') ? '2px solid var(--success)' :
                    status === 'deleted' ? '2px solid var(--danger)' : '2px solid transparent',
                transition: 'border-color 0.3s ease',
              },
              '&:hover fieldset': { border: '2px solid var(--border-hover)' },
              '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
              '&.Mui-focused': { backgroundColor: 'var(--bg-card)' },
            },
          }}
        />

        {/* Status bar + counter */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          {st ? (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1.25, py: 0.35, borderRadius: 'var(--radius-full)',
              bgcolor: st.bg, border: `1px solid ${st.border}`, color: st.color,
              transition: 'all 0.3s ease',
            }}>
              {st.icon}
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}>
                {st.label}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
              Se guarda automáticamente
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.68rem', color: (text || '').length > 900 ? 'var(--warning)' : 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontWeight: (text || '').length > 900 ? 700 : 400 }}>
            {(text || '').length}/1000
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
        {(text || '').length > 0 ? (
          <Button onClick={handleClear} size="small" startIcon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
            sx={{ borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', color: 'var(--danger)', '&:hover': { bgcolor: 'var(--danger-bg)' } }}>
            Borrar
          </Button>
        ) : <Box />}
        <Button onClick={onClose} size="small" variant="contained"
          sx={{ borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.82rem', textTransform: 'none', px: 3, backgroundColor: 'var(--accent)', boxShadow: 'none', '&:hover': { backgroundColor: 'var(--accent-hover)' } }}>
          Cerrar
        </Button>
      </Box>
    </Dialog>
  );
};

// ═══════════════════════════════════════
// Botón de observación (inline en CartItem)
// ═══════════════════════════════════════
const ObsButton = ({ item, onClick, compact = false }) => {
  const hasObs = !!item.observacion;
  const sz = compact ? 26 : 30;

  return (
    <Tooltip title={hasObs ? `Obs: ${item.observacion.substring(0, 50)}${item.observacion.length > 50 ? '...' : ''}` : "Agregar observación"} arrow>
      <IconButton onClick={onClick} size="small"
        sx={{
          width: sz, height: sz, borderRadius: 'var(--radius-sm)',
          bgcolor: hasObs ? '#eff6ff' : 'transparent',
          border: hasObs ? '1.5px solid #3b82f6' : '1.5px solid var(--border)',
          color: hasObs ? '#3b82f6' : 'var(--text-muted)',
          transition: 'all 0.2s ease',
          position: 'relative',
          '&:hover': { bgcolor: hasObs ? '#dbeafe' : 'var(--bg-subtle)', borderColor: '#3b82f6', color: '#3b82f6' },
        }}>
        <StickyNote2OutlinedIcon sx={{ fontSize: compact ? 13 : 15 }} />
        {/* Dot indicator */}
        {hasObs && (
          <Box sx={{ position: 'absolute', top: -2, right: -2, width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6', border: '1.5px solid var(--bg-card)' }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

// ═══════════════════════════════════════
// CartItem principal
// ═══════════════════════════════════════
const CartItem = ({
  item, isMobile, isCheckStock, changeAmount, handleDeleteClick, formatToCurrency,
}) => {
  const [obsModalOpen, setObsModalOpen] = useState(false);

  const isOutOfStock = isCheckStock && typeof item.availableStock === "number" && item.amount > item.availableStock;

  const accentBar = {
    "&::before": {
      content: '""', position: "absolute", top: "10px", bottom: "10px", left: 0,
      width: "3px", borderRadius: "0 3px 3px 0",
      bgcolor: isOutOfStock ? "var(--danger)" : "var(--accent)",
    },
  };

  const imgSrc = item.urls?.[0] || item.image || null;

  const Img = ({ size }) => (
    <Box sx={{ width: size, height: size, minWidth: size, flexShrink: 0, borderRadius: "var(--radius-sm)", bgcolor: "var(--bg-subtle)", border: "1.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      {imgSrc ? <Box component="img" src={imgSrc} alt={item.title} sx={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <InventoryIcon sx={{ color: "var(--text-muted)", fontSize: size * 0.38 }} />}
    </Box>
  );

  const DeleteBtn = ({ size = 30 }) => (
    <Tooltip title="Eliminar producto" arrow>
      <IconButton onClick={() => handleDeleteClick(item.id)} size="small"
        sx={{ width: size, height: size, borderRadius: "var(--radius-sm)", bgcolor: "var(--danger-bg)", color: "var(--danger)", border: "1.5px solid rgba(239,68,68,0.25)", flexShrink: 0, transition: "all var(--transition-fast)", "&:hover": { bgcolor: "rgba(239,68,68,0.15)", transform: "scale(1.05)" } }}>
        <DeleteOutlineIcon sx={{ fontSize: size * 0.52 }} />
      </IconButton>
    </Tooltip>
  );

  const StockWarn = ({ compact = false }) => !isOutOfStock ? null : (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.4 }}>
      <WarningAmberRoundedIcon sx={{ fontSize: compact ? 11 : 13, color: "var(--danger)" }} />
      <Typography sx={{ fontSize: compact ? "0.65rem" : "0.72rem", color: "var(--danger)", fontWeight: 600, fontFamily: "var(--font-body)" }}>
        {compact ? `Stock: ${item.availableStock}` : `Stock disponible: ${item.availableStock}`}
      </Typography>
    </Box>
  );

  // ── MOBILE ──
  if (isMobile) {
    return (
      <>
        <Box sx={{ px: 1.5, py: 1.5, position: "relative", ...accentBar }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.25 }}>
            <Img size={48} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "var(--text-primary)", fontFamily: "var(--font-body)", lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.title}</Typography>
              <Typography sx={{ fontSize: "0.67rem", color: "var(--text-muted)", fontFamily: "var(--font-mono)", mt: 0.2 }}>Cód: {item.codigo || item.id}</Typography>
              <StockWarn compact />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <ObsButton item={item} onClick={() => setObsModalOpen(true)} compact />
              <DeleteBtn size={28} />
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box>
              <Typography sx={{ fontSize: "0.6rem", color: "var(--text-muted)", fontFamily: "var(--font-body)", textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1, mb: "2px" }}>Precio</Typography>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>${formatToCurrency(item.price)}</Typography>
            </Box>
            <QtyControl item={item} changeAmount={changeAmount} isCheckStock={isCheckStock} compact />
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: "0.6rem", color: "var(--text-muted)", fontFamily: "var(--font-body)", textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1, mb: "2px" }}>Total</Typography>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-heading)" }}>${formatToCurrency(item.price * item.amount)}</Typography>
            </Box>
          </Box>
        </Box>
        <ObservacionModal open={obsModalOpen} onClose={() => setObsModalOpen(false)} item={item} />
      </>
    );
  }

  // ── DESKTOP ──
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 2, py: 1.5, position: "relative", ...accentBar }}>
        <Img size={60} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--text-primary)", fontFamily: "var(--font-body)", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{item.title}</Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "var(--text-muted)", fontFamily: "var(--font-mono)", mt: 0.25 }}>Cód: {item.codigo || item.id}</Typography>
          <StockWarn />
        </Box>

        <Box sx={{ minWidth: 90, textAlign: "right" }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>${formatToCurrency(item.price)}</Typography>
          <Typography sx={{ fontSize: "0.65rem", color: "var(--text-muted)", fontFamily: "var(--font-body)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Precio unit.</Typography>
        </Box>

        <QtyControl item={item} changeAmount={changeAmount} isCheckStock={isCheckStock} />

        <Box sx={{ minWidth: 100, textAlign: "right", flexShrink: 0 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "var(--accent)", fontFamily: "var(--font-heading)" }}>${formatToCurrency(item.price * item.amount)}</Typography>
          <Typography sx={{ fontSize: "0.65rem", color: "var(--text-muted)", fontFamily: "var(--font-body)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Total línea</Typography>
        </Box>

        <ObsButton item={item} onClick={() => setObsModalOpen(true)} />
        <DeleteBtn size={32} />
      </Box>
      <ObservacionModal open={obsModalOpen} onClose={() => setObsModalOpen(false)} item={item} />
    </>
  );
};

export default CartItem;